import centerOfMass from '@turf/center-of-mass'
import type { FeatureCollection } from 'geojson'
import { LucideEye } from 'lucide-react'
import { useMap } from 'react-map-gl'
import {
  aParcelDataVisible,
  aVisibleGeographies,
  useAtom,
  useSetAtom,
} from '~/atoms'
import { LoadingCircle } from '~/components/common'
import {
  Button,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui'
import { useGeographies } from '~/hooks/map'
import { cn } from '~/lib'
import type { Client } from '~/models'

type MapGeographiesControlProps = {
  client: Client
}

export default function MapGeographiesControl({
  client,
}: MapGeographiesControlProps) {
  const { isLoading, geographies, fileGeographies } = useGeographies(client)
  const [visibleGeographies, setVisibleGeographies] =
    useAtom(aVisibleGeographies)
  const setParcelDataVisible = useSetAtom(aParcelDataVisible)
  const hasGeographies = geographies.length > 0 || fileGeographies.length > 0

  return (
    <>
      {!hasGeographies ? (
        <>
          {isLoading ? (
            <div className="flex flex-col items-center">
              <LoadingCircle className="mb-2 h-6 w-6 text-primary" />
              Loading Layers
            </div>
          ) : (
            <>
              {!client.parcel_data_enabled && (
                <p className="">No geographies available for this client.</p>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {geographies.map((geo) => (
            <div
              className="flex flex-col gap-1 rounded-sm border p-4 shadow-sm"
              key={geo.type}>
              <div className="flex justify-between">
                <p className="overflow-ellipsis text-base">{geo.label}</p>
                <Switch
                  checked={visibleGeographies.includes(geo.type)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setParcelDataVisible(false)
                    }
                    setVisibleGeographies((prev) => {
                      if (checked) {
                        return [...prev, geo.type]
                      }
                      return prev.filter((type) => type !== geo.type)
                    })
                  }}
                />
              </div>
              {geo.description && (
                <p className="prose text-sm">{geo.description}</p>
              )}
            </div>
          ))}

          {fileGeographies.map((geo) => (
            <div
              className="relative flex flex-col gap-1 rounded-sm border p-4 shadow-sm"
              key={geo.properties.id}>
              <div className="flex justify-between">
                <p className="overflow-ellipsis text-base">
                  {geo.properties.name}
                </p>
                <div className="flex items-center gap-2">
                  {!geo.properties.description && (
                    <LayerZoomToButton geo={geo} />
                  )}
                  <Switch
                    checked={visibleGeographies.includes(geo.properties.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setParcelDataVisible(false)
                      }
                      setVisibleGeographies((prev) => {
                        if (checked) {
                          return [...prev, geo.properties.id]
                        }
                        return prev.filter((id) => id !== geo.properties.id)
                      })
                    }}
                  />
                </div>
              </div>
              {geo.properties.description && (
                <>
                  <p className="prose pr-4 text-sm">
                    {geo.properties.description}
                  </p>
                  <LayerZoomToButton
                    geo={geo}
                    className="absolute bottom-1 right-1"
                  />
                </>
              )}
            </div>
          ))}
        </>
      )}
    </>
  )
}

function LayerZoomToButton({
  geo,
  className,
}: {
  geo: FeatureCollection
  className?: string
}) {
  const mapRef = useMap()

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-auto w-auto p-2', className)}
            onClick={() => {
              const center = centerOfMass(geo)
              mapRef.current?.easeTo({
                center: [
                  center.geometry.coordinates[0],
                  center.geometry.coordinates[1],
                ],
                zoom: 12,
                duration: 500,
              })
            }}>
            <LucideEye className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">View on Map</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
